<template>
  <div>
    <a-page-header title="提取参考数据流">
      <template #extra>
        <a-button @click="$router.push({ name: 'reference-data-stream-logs' })">
          查看记录
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <!-- Worker 状态提示 -->
      <a-alert
        v-if="workerStatus !== null && !workerStatus.found"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
        :message="workerStatus.message"
      />
      <a-alert
        v-else-if="workerStatus !== null && workerStatus.found && !workerStatus.has_prompt_template"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
        message="Worker 已找到，但未配置提示词结构，请在 CallStep 管理中完成配置"
      />
      <a-alert
        v-else-if="workerStatus !== null && workerStatus.found"
        type="success"
        show-icon
        style="margin-bottom: 16px"
        :message="`Worker「${workerStatus.worker_name}」已就绪`"
      >
        <template #description>
          <div style="font-size: 12px; line-height: 1.8; color: #555">
            <span>Step Code：{{ workerStatus.step_code }}</span><br />
            <span>
              使用模型：{{ workerStatus.effective_model_name || '未知' }}
              <a-tag v-if="workerStatus.model_source" size="small" style="margin-left: 4px; font-size: 11px">
                {{ workerStatus.model_source }}
              </a-tag>
            </span>
          </div>
        </template>
      </a-alert>

      <!-- 操作区 -->
      <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap">
        <a-upload
          :before-upload="handleCsvSelect"
          :show-upload-list="false"
          accept=".csv,text/csv"
        >
          <a-button>
            <template #icon><UploadOutlined /></template>
            选择 CSV 文件
          </a-button>
        </a-upload>
        <span v-if="csvFileName" style="color: #52c41a; font-size: 13px">
          ✓ {{ csvFileName }}（{{ csvLineCount }} 行）
        </span>
        <a-button
          type="primary"
          :loading="extracting"
          :disabled="!csvContent || !workerStatus?.found || taskStatus === 'running'"
          @click="handleExtract"
        >
          开始提取
        </a-button>
        <a-button
          v-if="result"
          @click="handleCopyResult"
        >
          <template #icon><CopyOutlined /></template>
          复制结果
        </a-button>
      </div>

      <!-- CSV 预览 -->
      <div v-if="csvContent" style="margin-bottom: 16px">
        <div style="font-size: 12px; color: #888; margin-bottom: 4px">CSV 内容预览（前 5 行）</div>
        <div style="background: #fafafa; border: 1px solid #e8e8e8; border-radius: 4px; padding: 10px; font-family: monospace; font-size: 12px; white-space: pre-wrap; max-height: 120px; overflow-y: auto">{{ csvPreview }}</div>
      </div>

      <a-alert
        v-if="taskId"
        :type="taskStatus === 'failed' ? 'error' : taskStatus === 'success' ? 'success' : 'info'"
        show-icon
        style="margin-bottom: 16px"
        :message="taskStatusMessage"
      >
        <template #description>
          <div style="font-size: 12px; line-height: 1.8; color: #555">
            <span>任务 ID：{{ taskId }}</span><br />
            <span>当前状态：{{ taskStatusText }}</span>
            <span v-if="taskDurationMs !== null"><br />耗时：{{ taskDurationMs }} ms</span>
            <span v-if="taskErrorMessage"><br />错误信息：{{ taskErrorMessage }}</span>
          </div>
        </template>
      </a-alert>

      <!-- 结果展示 -->
      <div v-if="result">
        <div style="font-size: 13px; color: #666; margin-bottom: 8px">提取结果：</div>
        <div style="background: #f5f5f5; border-radius: 4px; padding: 16px; max-height: 600px; overflow-y: auto">
          <pre style="margin: 0; white-space: pre-wrap; word-wrap: break-word; font-family: 'Courier New', monospace; font-size: 13px">{{ result }}</pre>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import { computed, ref, onBeforeUnmount, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { UploadOutlined, CopyOutlined } from '@ant-design/icons-vue';
import { getWorkerStatus, extractReferenceDataStream, getReferenceDataStreamTask } from '@/api/referenceDataStream';

export default {
  name: 'ReferenceDataStreamPage',
  components: { UploadOutlined, CopyOutlined },
  setup() {
    const workerStatus = ref(null);
    const csvContent = ref('');
    const csvFileName = ref('');
    const csvLineCount = ref(0);
    const csvPreview = ref('');
    const extracting = ref(false);
    const result = ref('');
    const taskId = ref('');
    const taskStatus = ref('');
    const taskErrorMessage = ref('');
    const taskDurationMs = ref(null);
    let pollingTimer = null;

    const taskStatusText = computed(() => {
      const statusMap = {
        pending: '排队中',
        running: '处理中',
        success: '已完成',
        failed: '执行失败'
      };
      return statusMap[taskStatus.value] || taskStatus.value || '-';
    });

    const taskStatusMessage = computed(() => {
      if (taskStatus.value === 'success') {
        return '异步提取已完成';
      }
      if (taskStatus.value === 'failed') {
        return '异步提取失败';
      }
      if (taskStatus.value === 'running') {
        return '异步提取进行中';
      }
      return '异步任务已提交';
    });

    const stopPolling = () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
        pollingTimer = null;
      }
    };

    const pollTaskStatus = async (targetTaskId, { silent = false } = {}) => {
      try {
        const res = await getReferenceDataStreamTask(targetTaskId);
        if (res.code !== 200) {
          return;
        }

        taskStatus.value = res.data.status;
        taskDurationMs.value = res.data.durationMs ?? null;
        taskErrorMessage.value = res.data.errorMessage || '';

        if (res.data.status === 'success') {
          result.value = res.data.result || '';
          extracting.value = false;
          stopPolling();
          if (!silent) {
            message.success('提取完成');
          }
          return;
        }

        if (res.data.status === 'failed') {
          result.value = '';
          extracting.value = false;
          stopPolling();
          if (!silent) {
            message.error(res.data.errorMessage || '提取失败');
          }
          return;
        }

        extracting.value = true;
      } catch (error) {
        console.error('轮询任务状态失败:', error);
      }
    };

    const startPolling = (targetTaskId) => {
      stopPolling();
      pollTaskStatus(targetTaskId, { silent: true });
      pollingTimer = setInterval(() => {
        pollTaskStatus(targetTaskId);
      }, 3000);
    };

    const loadWorkerStatus = async () => {
      try {
        const res = await getWorkerStatus();
        if (res.code === 200) {
          workerStatus.value = res.data;
        }
      } catch (error) {
        console.error('加载 Worker 状态失败:', error);
      }
    };

    const handleCsvSelect = (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        csvContent.value = text;
        csvFileName.value = file.name;
        const lines = text.split('\n').filter(l => l.trim());
        csvLineCount.value = lines.length;
        csvPreview.value = lines.slice(0, 5).join('\n');
        result.value = '';
        taskId.value = '';
        taskStatus.value = '';
        taskErrorMessage.value = '';
        taskDurationMs.value = null;
        stopPolling();
      };
      reader.readAsText(file, 'utf-8');
      return false;
    };

    const handleExtract = async () => {
      if (!csvContent.value) {
        message.warning('请先选择 CSV 文件');
        return;
      }
      extracting.value = true;
      result.value = '';
      taskErrorMessage.value = '';
      taskDurationMs.value = null;
      stopPolling();
      try {
        const res = await extractReferenceDataStream(csvContent.value, csvFileName.value, csvLineCount.value);
        if (res.code === 200) {
          taskId.value = res.data.taskId;
          taskStatus.value = res.data.status;
          message.success('任务已提交，开始轮询处理结果');
          startPolling(res.data.taskId);
        } else {
          message.error(res.message || '提取失败');
          extracting.value = false;
        }
      } catch (error) {
        message.error('提取失败：' + (error.message || '未知错误'));
        extracting.value = false;
      }
    };

    const handleCopyResult = () => {
      if (!result.value) return;
      try {
        navigator.clipboard.writeText(result.value);
        message.success('已复制到剪贴板');
      } catch {
        const textArea = document.createElement('textarea');
        textArea.value = result.value;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('已复制到剪贴板');
      }
    };

    onMounted(() => {
      loadWorkerStatus();
    });

    onBeforeUnmount(() => {
      stopPolling();
    });

    return {
      workerStatus,
      csvContent,
      csvFileName,
      csvLineCount,
      csvPreview,
      extracting,
      result,
      taskId,
      taskStatus,
      taskStatusText,
      taskStatusMessage,
      taskErrorMessage,
      taskDurationMs,
      handleCsvSelect,
      handleExtract,
      handleCopyResult
    };
  }
};
</script>
